import { SettingsActionRow } from '@account/components';
import { AccountConnectionDialog } from '@account/routes/AccountConnections';
import {
    useCreateDropboxTokenMutation,
    useDeleteDropboxTokenMutation,
    useGetDropboxTokensQuery,
} from '@account/services';
import { DropboxFolderType, DropboxToken } from '@account/types';
import { getFolderTypeInfo } from '@account/utils/account';
import { RepairFiles } from '@assets/icons/RepairFiles';
import { Alert } from '@components/display/alerts';
import { IOSSwitch } from '@components/utility/switches';
import { useAuth } from '@hooks/useAuth';
import AutoStoriesOutlinedIcon from '@mui/icons-material/AutoStoriesOutlined';
import MemoryIcon from '@mui/icons-material/Memory';
import { AlertTitle, CircularProgress, Stack } from '@mui/material';
import { handleError, toaster } from '@utils/common';
import { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

export const AccountConnections = () => {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedType, setSelectedType] = useState<DropboxFolderType | null>(null);

    //#region Data Fetching
    const { user } = useAuth();

    const { data: dropboxTokens, isLoading: isLoadingDropboxTokens } = useGetDropboxTokensQuery();
    const [createDropboxToken, { isLoading: isCreatingDropboxToken }] = useCreateDropboxTokenMutation();
    const [deleteDropboxToken, { isLoading: isDeletingDropboxToken }] = useDeleteDropboxTokenMutation();
    //#endregion

    //#region Hooks
    const intl = useIntl();
    //#endregion

    //#region Processed Data
    const tokens = useMemo(() => (dropboxTokens?.results as DropboxToken[] | undefined) ?? [], [dropboxTokens]);
    const allTokens = useMemo(() => tokens.map((token) => token.sync_type), [tokens]);
    const validTokens = useMemo(
        () => tokens.filter((token) => token.is_active && !token.is_expired).map((token) => token.sync_type),
        [tokens]
    );
    const hasErrors = useMemo(
        () => !!tokens.length && tokens.some((token) => !token.is_active || token.is_expired),
        [tokens]
    );
    const selectedTypeIsConnected = selectedType ? validTokens.includes(selectedType) : false;
    const selectedToken = useMemo(
        () => (selectedType ? tokens.find((token) => token.sync_type === selectedType) : undefined),
        [selectedType, tokens]
    );
    //#endregion

    //#region Handlers
    const openDialogForType = (type: DropboxFolderType) => {
        setSelectedType(type);
        setDialogOpen(true);
    };

    const handleCreateToken = async ({ folderType, code }: { folderType: DropboxFolderType; code: string }) => {
        const { title: name } = getFolderTypeInfo(intl, folderType);
        try {
            await createDropboxToken({ type: folderType, code }).unwrap();
            toaster.success({
                title: intl.formatMessage(
                    {
                        id: 'account.connections.dropboxTokenCreationSuccess',
                        defaultMessage: '{name} connection created',
                    },
                    { name }
                ),
                msg: intl.formatMessage(
                    {
                        id: 'account.connections.dropboxTokenCreationSuccessMessage',
                        defaultMessage: 'The {name} connection has been successfully created.',
                    },
                    { name }
                ),
            });
        } catch (err) {
            handleError(
                intl.formatMessage(
                    {
                        id: 'account.connections.dropboxTokenCreationError',
                        defaultMessage: 'An error occurred while creating the {name} connection. Please try again.',
                    },
                    { name }
                ),
                err
            );
        }
    };

    const handleDeleteToken = async () => {
        if (!selectedToken) return;
        const { title: name } = getFolderTypeInfo(intl, selectedToken.sync_type);
        try {
            await deleteDropboxToken({ id: selectedToken.id }).unwrap();
            toaster.success({
                title: intl.formatMessage(
                    {
                        id: 'account.connections.dropboxTokenDeletionSuccess',
                        defaultMessage: '{name} connection deleted',
                    },
                    { name }
                ),
                msg: intl.formatMessage(
                    {
                        id: 'account.connections.dropboxTokenDeletionSuccessMessage',
                        defaultMessage: 'The {name} connection has been successfully deleted.',
                    },
                    { name }
                ),
            });
        } catch (err) {
            handleError(
                intl.formatMessage(
                    {
                        id: 'account.connections.dropboxTokenDeletionError',
                        defaultMessage: 'An error occurred while deleting the {name} connection. Please try again.',
                    },
                    { name }
                ),
                err
            );
        }
    };
    //#endregion

    //#region Renderers
    const connections = useMemo(
        () => [
            {
                icon: <MemoryIcon sx={{ fontSize: 30 }} />,
                title: intl.formatMessage({
                    id: 'account.connections.firmwareFiles.title',
                    defaultMessage: 'Firmware Files',
                }),
                type: 'firmwares' as DropboxFolderType,
            },
            {
                icon: <AutoStoriesOutlinedIcon sx={{ fontSize: 30 }} />,
                title: intl.formatMessage({
                    id: 'account.connections.manuals.title',
                    defaultMessage: 'Manuals',
                }),
                type: 'manuals' as DropboxFolderType,
            },
            ...(user?.exact_debtor_number
                ? [
                      {
                          icon: <RepairFiles />,
                          title: intl.formatMessage({
                              id: 'account.connections.repairFiles.title',
                              defaultMessage: 'Repair Files',
                          }),
                          type: 'repairs' as DropboxFolderType,
                      },
                  ]
                : []),
        ],
        [user, intl]
    );
    //#endregion

    return (
        <>
            <title>My Connections - Victron Professional</title>
            <Stack gap="24px">
                {hasErrors && (
                    <Alert.Error>
                        <AlertTitle>
                            <FormattedMessage
                                id="account.connections.connectionError"
                                defaultMessage="Connection Error"
                            />
                        </AlertTitle>
                        <FormattedMessage
                            id="account.connections.connectionErrorMessage"
                            defaultMessage="One of your connections has encountered an issue. Please re-connect to restore service."
                        />
                    </Alert.Error>
                )}
                {connections.map((connection) => (
                    <SettingsActionRow
                        key={connection.type}
                        icon={connection.icon}
                        title={connection.title}
                        description={
                            <FormattedMessage
                                id="account.connections.description"
                                defaultMessage="Connect to our shared folder to always have access to the latest documents. Easily send customers a link to the right document, straight from your phone."
                            />
                        }
                        onClick={() => openDialogForType(connection.type)}
                        trailingAction={
                            isLoadingDropboxTokens ? (
                                <CircularProgress size={20} />
                            ) : (
                                <IOSSwitch
                                    size="small"
                                    checked={allTokens.includes(connection.type)}
                                    color={validTokens.includes(connection.type) ? 'primary' : 'error'}
                                />
                            )
                        }
                        trailingActionFullWidthOnMobile={false}
                    />
                ))}
            </Stack>
            <AccountConnectionDialog
                open={dialogOpen}
                folderType={selectedType}
                scenario={selectedTypeIsConnected ? 'delete' : 'create'}
                onCreate={handleCreateToken}
                onDelete={handleDeleteToken}
                createLoading={isCreatingDropboxToken}
                deleteLoading={isDeletingDropboxToken}
                onClose={() => {
                    setDialogOpen(false);
                }}
                onExited={() => setSelectedType(null)}
            />
        </>
    );
};
